/**
 * Students API + TanStack Query hooks.
 * Endpoint catalog: docs/API-CONTRACT.md → "Students".
 *
 * Detail / write / mutation hooks extend the original list-only shape so the
 * `/students` page can read, create, update, delete, and assign tutors
 * without bypassing the TanStack cache. Cache invalidation is explicit on
 * every mutation so the list, detail, and dashboard counts all refresh.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "./client";
import { toQueryString, type ListQuery, type Paginated } from "./types";

/* -------------------------------------------------------------- list / row */

export interface StudentListItem {
  id: number | string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  /** Null for a child a parent created: they sign in with `username`. */
  email?: string | null;
  username?: string | null;
  /** True until the admin gives this student their Google Meet link. */
  needs_meet_link?: boolean;
  /** True once a parent has agreed to lessons being recorded. */
  recording_consent?: boolean;
  avatar?: string | null;
  year_group?: string | null;
  status?: string | null;
  school_name?: string;
  next_lesson?: NextLessonStub | null;
  pending_homework_count?: number;
  submitted_homework_count?: number;
  parent?: { id: number | string; full_name?: string } | null;
  created_at?: string;
  updated_at?: string;
  external_id?: string | null;
}

export interface NextLessonStub {
  id: number | string;
  subject?: string;
  scheduled_at: string;
  tutor_name?: string;
}

export interface StudentListQuery extends ListQuery {
  status?: string;
  year_group?: string | number;
  /** Backend filter key is `assigned_tutors`; we accept the friendlier alias too. */
  tutor?: number | string;
  assigned_tutors?: number | string;
}

export function listStudents(
  q: StudentListQuery = {},
): Promise<Paginated<StudentListItem>> {
  // Map the friendlier `tutor` alias to the backend's `assigned_tutors` filter.
  const { tutor, ...rest } = q;
  const params: Record<string, string | number | boolean | undefined> = {
    ...rest,
  };
  if (tutor !== undefined && tutor !== null && tutor !== "") {
    params.assigned_tutors = tutor;
  }
  return api
    .get<Paginated<StudentListItem>>(`/students/${toQueryString(params)}`)
    .then((r) => r.data);
}

export function useStudents(
  q: StudentListQuery = {},
  enabled = true,
): UseQueryResult<Paginated<StudentListItem>> {
  return useQuery({
    queryKey: ["students", q],
    queryFn: () => listStudents(q),
    enabled,
    // Search, filter and "Show more" keep the current list on screen.
    placeholderData: keepPreviousData,
  });
}

/* -------------------------------------------------------------- detail */

/**
 * Mirror of `StudentDetailSerializer`. Every field is optional so a partial
 * response (e.g. legacy import without a year_group) doesn't break the UI.
 */
export interface StudentDetail {
  id: number | string;
  user: {
    id: number | string;
    /** Null for a child a parent created: they sign in with `username`. */
    email?: string | null;
    username?: string | null;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    phone?: string;
    avatar?: string | null;
  };
  gender?: string;
  date_of_birth?: string | null;
  phone_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  guardian_name?: string;
  /** mother, father, guardian, carer or other; blank when not given. */
  guardian_relationship?: string;
  guardian_contact?: string;
  school_name?: string;
  /** Year group `code` (slug). */
  year_group?: string | null;
  /** Subjects as `code` slugs. */
  subjects_enrolled?: string[];
  assigned_tutors?: Array<number | string>;
  parents?: Array<{ id: number | string; email?: string; full_name?: string }>;
  /** Who can sign in. Admins only. */
  access?: StudentAccess;
  /** One-time sign-in links, only on the save that created them. */
  links?: { parent: string | null; student: string | null };
  enrolment_date?: string | null;
  status?: string | null;
  notes?: string;
  timezone?: string;
  created_at?: string;
  updated_at?: string;
  external_id?: string | null;

  // Onboarding (Sept 2026): parent-created child profiles
  /** Permanent Google Meet room for this student. */
  meet_link?: string;
  access_needs?: string;
  /** Optional contact address for reminders. Never a sign-in. */
  contact_email?: string;
  subject_rows?: StudentSubjectRow[];
  /** True once a parent has agreed to lessons being recorded. */
  recording_consent?: boolean;
  /** Who agreed to DrTutor holding the child's details, and when. */
  consent?: {
    given_at: string;
    given_by: string | null;
    version: string;
    recording_given_at: string | null;
    recording_given_by: string | null;
    recording_version: string;
  } | null;
  /** viewable: the parent can see the generated password. child_set: the
   *  child chose their own. none: not a parent-managed login. */
  credential_state?: "viewable" | "child_set" | "none";
}

export interface LookupRef {
  id: number;
  code: string;
  name: string;
}

export interface StudentSubjectRow {
  subject: LookupRef;
  exam_board: LookupRef | null;
  current_grade: string;
  target_grade: string;
  /** The tutor allocated to this subject. */
  tutor?: { id: number; full_name: string } | null;
}

/** Write shape for a subject row: lookup ids. */
export interface StudentSubjectRowInput {
  subject: number;
  exam_board?: number | null;
  current_grade?: string;
  target_grade?: string;
}

export function getStudentDetail(
  id: number | string,
): Promise<StudentDetail> {
  return api
    .get<StudentDetail>(`/students/${encodeURIComponent(String(id))}/`)
    .then((r) => r.data);
}

export function useStudentDetail(
  id: number | string | null | undefined,
  enabled = true,
): UseQueryResult<StudentDetail> {
  return useQuery({
    queryKey: ["student-detail", String(id)],
    queryFn: () => getStudentDetail(id as number | string),
    enabled: Boolean(id) && enabled,
  });
}

/* -------------------------------------------------------------- write */

/**
 * Payload shape sent to `POST /students/` and `PATCH /students/{id}/`.
 * Mirrors `StudentWriteSerializer`. For new students, `email` (and ideally
 * `first_name` / `last_name`) is required so the backend can create the
 * underlying `accounts.User`. Subjects, status, year_group, and tutor ids
 * are PKs (integers), per the write serializer.
 */
export interface StudentWritePayload {
  // Create-only user fields
  email?: string;
  first_name?: string;
  last_name?: string;
  password?: string;

  // Student profile
  gender?: string;
  date_of_birth?: string | null;
  phone_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  guardian_name?: string;
  /** mother, father, guardian, carer or other; blank when not given. */
  guardian_relationship?: string;
  guardian_contact?: string;
  /** Creates or links the parent account; not stored on the student. */
  guardian_email?: string;
  school_name?: string;
  year_group?: number | null;
  subjects_enrolled?: number[];
  assigned_tutors?: number[];
  enrolment_date?: string | null;
  status?: number | null;
  notes?: string;
  timezone?: string;
  /** Must start https://meet.google.com/ (or be blank). */
  meet_link?: string;
  access_needs?: string;
  contact_email?: string;
  /** Replaces every per-subject row; keeps subjects_enrolled in step. */
  subject_rows?: StudentSubjectRowInput[];
}

export function useCreateStudent(): UseMutationResult<
  StudentDetail,
  Error,
  StudentWritePayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      api.post<StudentDetail>("/students/", payload).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["students"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useUpdateStudent(): UseMutationResult<
  StudentDetail,
  Error,
  { id: number | string; data: StudentWritePayload }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) =>
      api
        .patch<StudentDetail>(
          `/students/${encodeURIComponent(String(id))}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: (data, vars) => {
      qc.setQueryData(["student-detail", String(vars.id)], data);
      void qc.invalidateQueries({ queryKey: ["students"] });
    },
  });
}

export function useDeleteStudent(): UseMutationResult<
  void,
  Error,
  number | string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      api
        .delete<void>(`/students/${encodeURIComponent(String(id))}/`)
        .then(() => undefined),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ["students"] });
      void qc.invalidateQueries({ queryKey: ["student-detail", String(id)] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/* -------------------------------------------------------------- allocation */

/** Allocate a tutor to one subject (null clears it). Admin only. */
export function allocateTutor(studentId: number | string, subject: number, tutor: number | null): Promise<StudentDetail> {
  return api
    .post<StudentDetail>(`/students/${encodeURIComponent(String(studentId))}/allocate/`, { subject, tutor })
    .then((r) => r.data);
}

export function useAllocateTutor(
  studentId: number | string,
): UseMutationResult<StudentDetail, Error, { subject: number; tutor: number | null }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, tutor }) => allocateTutor(studentId, subject, tutor),
    onSuccess: (data) => {
      qc.setQueryData<StudentDetail>(["student-detail", String(studentId)], (prev) => ({ ...prev, ...data }));
      void qc.invalidateQueries({ queryKey: ["students"] });
    },
  });
}

/* -------------------------------------------------------------- assign tutor */

export function useAssignTutor(
  studentId: number | string,
): UseMutationResult<StudentDetail, Error, number | string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tutor_id) =>
      api
        .post<StudentDetail>(
          `/students/${encodeURIComponent(String(studentId))}/assign-tutor/`,
          { tutor_id },
        )
        .then((r) => r.data),
    onSuccess: (data) => {
      // Keep `access`, which only the detail read carries.
      qc.setQueryData<StudentDetail>(["student-detail", String(studentId)], (prev) => ({ ...prev, ...data }));
      void qc.invalidateQueries({ queryKey: ["students"] });
    },
  });
}

export function useUnassignTutor(
  studentId: number | string,
): UseMutationResult<void, Error, number | string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tutor_id) =>
      api
        .delete<void>(
          `/students/${encodeURIComponent(String(studentId))}/assign-tutor/${encodeURIComponent(String(tutor_id))}/`,
        )
        .then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["student-detail", String(studentId)] });
      void qc.invalidateQueries({ queryKey: ["students"] });
    },
  });
}

/* -------------------------------------------------------------- sign-in access */

/** One person's sign-in state. Backend: `students/access.py`. */
export interface AccessPerson {
  user_id: number;
  email: string;
  name: string;
  /** active: has a password. invited: live link out. expired: needs a new link. */
  state: "active" | "invited" | "expired";
  expires_at: string | null;
}

export interface StudentAccess {
  student: AccessPerson;
  parents: AccessPerson[];
}

/**
 * Mint a fresh one-time sign-in link to copy. Nothing is emailed, and the
 * previous link for that person stops working.
 */
export function useStudentAccessLink(
  studentId: number | string,
): UseMutationResult<
  { invite_url: string; access: StudentAccess },
  Error,
  { recipient: "student" | "parent"; parent_id?: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      api
        .post<{ invite_url: string; access: StudentAccess }>(
          `/students/${encodeURIComponent(String(studentId))}/access-link/`,
          vars,
        )
        .then((r) => r.data),
    onSuccess: (res) => {
      qc.setQueryData<StudentDetail>(["student-detail", String(studentId)], (prev) =>
        prev ? { ...prev, access: res.access } : prev,
      );
    },
  });
}

/* -------------------------------------------------------------- per-student lessons / homework */

/**
 * Lightweight lesson shape returned by `GET /students/{id}/lessons/`.
 * The detail drawer renders the most recent few. Backend reuses the lesson
 * list serializer, so this is a structural subset.
 */
export interface StudentLessonItem {
  id: number | string;
  title?: string;
  subject?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  status?: string;
  tutor?: { id: number | string; full_name?: string } | null;
  tutor_name?: string;
}

export function useStudentLessons(
  id: number | string | null | undefined,
  enabled = true,
): UseQueryResult<Paginated<StudentLessonItem>> {
  return useQuery({
    queryKey: ["student-lessons", id],
    queryFn: () =>
      api
        .get<Paginated<StudentLessonItem>>(
          `/students/${encodeURIComponent(String(id))}/lessons/?page_size=5&ordering=-scheduled_at`,
        )
        .then((r) => r.data),
    enabled: Boolean(id) && enabled,
  });
}

export interface StudentHomeworkItem {
  id: number | string;
  title?: string;
  subject?: string;
  status?: string;
  /** The homework list serializer names this `due_date`. */
  due_date?: string | null;
  tutor_name?: string;
}

export function useStudentHomework(
  id: number | string | null | undefined,
  enabled = true,
): UseQueryResult<Paginated<StudentHomeworkItem>> {
  return useQuery({
    queryKey: ["student-homework", id],
    queryFn: () =>
      api
        .get<Paginated<StudentHomeworkItem>>(
          `/students/${encodeURIComponent(String(id))}/homework/?page_size=50`,
        )
        .then((r) => {
          // The endpoint takes no status or ordering filters (a status code
          // there is a 400), so order by due date here: soonest first, work
          // without a date last.
          const results = [...r.data.results].sort((a, b) =>
            (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999"),
          );
          return { ...r.data, results };
        }),
    enabled: Boolean(id) && enabled,
  });
}

/* -------------------------------------------------------------- own profile */

/**
 * The signed-in student's own record. `GET /students/` returns only
 * themselves for a student, so the first row is them; the detail read then
 * carries the Meet link.
 */
export function useMyStudentProfile(enabled = true): UseQueryResult<StudentDetail | null> {
  return useQuery({
    queryKey: ["student-detail", "me"],
    queryFn: async () => {
      const list = await listStudents({ page_size: 1 });
      const me = list.results[0];
      return me ? getStudentDetail(me.id) : null;
    },
    enabled,
  });
}

/* -------------------------------------------------------------- child password (admin) */

export interface ChildPasswordReset {
  password: string;
  username: string | null;
  credential_state: "viewable" | "child_set" | "none";
}

/**
 * Admin (or the child's parent) gives a username child a fresh generated
 * password. The old one stops working. Audited on the backend.
 */
export function useResetChildPassword(
  studentId: number | string,
): UseMutationResult<ChildPasswordReset, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api
        .post<ChildPasswordReset>(
          `/family/children/${encodeURIComponent(String(studentId))}/reset-password/`,
        )
        .then((r) => r.data),
    onSuccess: (res) => {
      qc.setQueryData<StudentDetail>(["student-detail", String(studentId)], (prev) =>
        prev ? { ...prev, credential_state: res.credential_state } : prev,
      );
    },
  });
}
